import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import './GalvanizedTrailersPage.css'

interface TrailerModel {
  model: string;
  capacity: number;
  boatLength: string;
  overallLength: string;
  bowEyeMin?: string;
  bowEyeMax?: string;
  gvwr: number;
  tongueJack: string;
  axles: number;
  brakes: string;
  tireSize: string;
  overallWidth: string;
  betweenFenders: string;
  rollers?: number;
}

interface PWCModel {
  model: string;
  capacity: number;
  seating: string;
  overallLength: string;
  gvwr: number;
  tongueJack: string;
  axles: number;
  brakes: string;
  tireSize: string;
  overallWidth: string;
  betweenFenders: string;
}

interface PontoonModel {
  model: string;
  capacity: number;
  boatLength: string;
  overallLength: string;
  minBunkWidth: string;
  maxBunkWidth: string;
  gvwr: number;
  tongueJack: string;
  axles: number;
  brakes: string;
  tireSize: string;
  overallWidth: string;
  betweenFenders: string;
}

const GalvanizedTrailersPage: React.FC = () => {
  // Bunk Trailers
  const singleAxleBunkModels: TrailerModel[] = [
    {
      model: "VB-1000",
      capacity: 1000,
      boatLength: "12'-14'",
      overallLength: "15'9\"",
      bowEyeMin: "8'6\"",
      bowEyeMax: "13'",
      gvwr: 1270,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    },
    {
      model: "VB-3300",
      capacity: 3300,
      boatLength: "20'-21'",
      overallLength: "23'6\"",
      bowEyeMin: "16'",
      bowEyeMax: "20'",
      gvwr: 4175,
      tongueJack: "Single Wheel",
      axles: 1,
      brakes: "Disc",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    }
  ];

  const tandemAxleBunkModels: TrailerModel[] = [
    {
      model: "VBT-3600S",
      capacity: 3600,
      boatLength: "18'-20'",
      overallLength: "22'6\"",
      bowEyeMin: "15'",
      bowEyeMax: "19'",
      gvwr: 4600,
      tongueJack: "Single Wheel",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VBT-7900",
      capacity: 7900,
      boatLength: "25'-27'",
      overallLength: "29'10\"",
      bowEyeMin: "20'",
      bowEyeMax: "25'",
      gvwr: 9350,
      tongueJack: "2000lb. Foot",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    }
  ];

  const tripleAxleBunkModels: TrailerModel[] = [
    {
      model: "VBT-8600",
      capacity: 8600,
      boatLength: "27'-28'",
      overallLength: "31'6\"",
      bowEyeMin: "22'",
      bowEyeMax: "26'6\"",
      gvwr: 10420,
      tongueJack: "2000lb. Foot",
      axles: 3,
      brakes: "Disc-2",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VBT-10800",
      capacity: 10800,
      boatLength: "29'-31'",
      overallLength: "32'11\"",
      bowEyeMin: "22'",
      bowEyeMax: "28'",
      gvwr: 12640,
      tongueJack: "2000lb. Foot",
      axles: 3,
      brakes: "Disc-2",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    }
  ];

  // PWC Trailers
  const pwcModels: PWCModel[] = [
    {
      model: "VW-1200",
      capacity: 1200,
      seating: "2/3 Seater",
      overallLength: "13'",
      gvwr: 1420,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    },
    {
      model: "VW-2000",
      capacity: 2000,
      seating: "Double",
      overallLength: "18'",
      gvwr: 2420,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "20.5 X 8.0 X 10 LRD 5H",
      overallWidth: "102\"",
      betweenFenders: "84\""
    }
  ];

  // Pontoon Trailers
  const singleAxlePontoonModels: PontoonModel[] = [
    {
      model: "VP-18-20",
      capacity: 2000,
      boatLength: "17'-19'",
      overallLength: "23'3\"",
      minBunkWidth: "72\"",
      maxBunkWidth: "80\"",
      gvwr: 2760,
      tongueJack: "Optional",
      axles: 1,
      brakes: "Optional",
      tireSize: "ST175 80R13 LRD",
      overallWidth: "98\"",
      betweenFenders: "78\""
    },
    {
      model: "VP-24-27",
      capacity: 2700,
      boatLength: "22'-25'",
      overallLength: "31'",
      minBunkWidth: "72\"",
      maxBunkWidth: "80\"",
      gvwr: 3660,
      tongueJack: "Optional",
      axles: 1,
      brakes: "Optional",
      tireSize: "ST215 75R14 LRC",
      overallWidth: "98\"",
      betweenFenders: "78\""
    }
  ];

  const tandemAxlePontoonModels: PontoonModel[] = [
    {
      model: "VPT-22-50",
      capacity: 5000,
      boatLength: "19'-22'",
      overallLength: "26'3\"",
      minBunkWidth: "72\"",
      maxBunkWidth: "80\"",
      gvwr: 6120,
      tongueJack: "Single Wheel",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST185 80R13 LRD",
      overallWidth: "98\"",
      betweenFenders: "78\""
    },
    {
      model: "VPT-26-50",
      capacity: 5000,
      boatLength: "23'-26'",
      overallLength: "32'",
      minBunkWidth: "72\"",
      maxBunkWidth: "80\"",
      gvwr: 6160,
      tongueJack: "Single Wheel",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST185 80R13 LRD",
      overallWidth: "98\"",
      betweenFenders: "78\""
    }
  ];

  const tripleAxlePontoonModels: PontoonModel[] = [
    {
      model: "VPT-26-65",
      capacity: 6500,
      boatLength: "23'-26'",
      overallLength: "32'6\"",
      minBunkWidth: "72\"",
      maxBunkWidth: "80\"",
      gvwr: 8100,
      tongueJack: "2000lb Foot",
      axles: 3,
      brakes: "Disc-2",
      tireSize: "ST175 80R 13LRC",
      overallWidth: "98\"",
      betweenFenders: "78\""
    }
  ];

  // Roller Trailers
  const singleAxleRollerModels: TrailerModel[] = [
    {
      model: "VR-1550",
      capacity: 1550,
      boatLength: "14'-15'",
      overallLength: "16'5\"",
      bowEyeMin: "10'6\"",
      bowEyeMax: "14'6\"",
      gvwr: 1950,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "5.30 x 12 LRC 5H",
      overallWidth: "78\"",
      betweenFenders: "60\"",
      rollers: 12
    },
    {
      model: "VR-3350",
      capacity: 3350,
      boatLength: "20'-21'",
      overallLength: "23'6\"",
      bowEyeMin: "16'",
      bowEyeMax: "20'",
      gvwr: 4275,
      tongueJack: "Single Wheel",
      axles: 1,
      brakes: "Disc",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\"",
      rollers: 20
    }
  ];

  const tandemAxleRollerModels: TrailerModel[] = [
    {
      model: "VRT-3650S",
      capacity: 3650,
      boatLength: "18'-20'",
      overallLength: "22'6\"",
      bowEyeMin: "16'",
      bowEyeMax: "19'",
      gvwr: 4650,
      tongueJack: "Single Wheel",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\"",
      rollers: 20
    },
    {
      model: "VRT-7950",
      capacity: 7950,
      boatLength: "25'-27'",
      overallLength: "28'5\"",
      bowEyeMin: "20'",
      bowEyeMax: "25'",
      gvwr: 9550,
      tongueJack: "2000lb. Foot",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\"",
      rollers: 64
    }
  ];

  const tripleAxleRollerModels: TrailerModel[] = [
    {
      model: "VRT-8650",
      capacity: 8650,
      boatLength: "27'-28'",
      overallLength: "31'6\"",
      bowEyeMin: "22'",
      bowEyeMax: "27'6\"",
      gvwr: 10450,
      tongueJack: "2000lb. Foot",
      axles: 3,
      brakes: "Disc-2",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\"",
      rollers: 64
    },
    {
      model: "VRT-10850",
      capacity: 10850,
      boatLength: "29'-31'",
      overallLength: "32'11\"",
      bowEyeMin: "22'",
      bowEyeMax: "29'",
      gvwr: 12750,
      tongueJack: "2000lb. Foot",
      axles: 3,
      brakes: "Disc-2",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\"",
      rollers: 64
    }
  ];

  // Tritoon Models (same as pontoon but with TRI designation)
  const tritoonModels: PontoonModel[] = [
    {
      model: "VP-18-20TRI",
      capacity: 2000,
      boatLength: "17'-19'",
      overallLength: "23'3\"",
      minBunkWidth: "72\"",
      maxBunkWidth: "80\"",
      gvwr: 2840,
      tongueJack: "Optional",
      axles: 1,
      brakes: "Optional",
      tireSize: "ST175 80R 13LRD",
      overallWidth: "98\"",
      betweenFenders: "78\""
    },
    {
      model: "VPT-26-65TRI",
      capacity: 6500,
      boatLength: "23'-26'",
      overallLength: "36'6\"",
      minBunkWidth: "72\"",
      maxBunkWidth: "80\"",
      gvwr: 8150,
      tongueJack: "2000lb Foot",
      axles: 3,
      brakes: "Disc-2",
      tireSize: "ST175 80R 13LRC",
      overallWidth: "98\"",
      betweenFenders: "78\""
    }
  ];

  // Skiff and Jon Boat Models (using single axle bunk models)
  const skiffModels: TrailerModel[] = [
    {
      model: "VB-1000",
      capacity: 1000,
      boatLength: "12'-14'",
      overallLength: "15'9\"",
      bowEyeMin: "8'6\"",
      bowEyeMax: "13'",
      gvwr: 1270,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    },
    {
      model: "VB-1800",
      capacity: 1800,
      boatLength: "15'-16'",
      overallLength: "19'2\"",
      bowEyeMin: "10'6\"",
      bowEyeMax: "16'6\"",
      gvwr: 2180,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "5.30 x 12 LRD 5H",
      overallWidth: "78\"",
      betweenFenders: "60\""
    }
  ];

  const galvanizedFeatures = [
    "Plug & Play Lights with waterproof connections",
    "Aluminum Diamond Plate Fenders for durability",
    "Heavy Duty Winch Straps for secure transport",
    "D.O.T. Composite Brake Line with Brass Fittings",
    "Balanced Radial Tires for smooth towing",
    "Eliminator GalvX Vented Rotor Disc Brakes",
    "Super Lube Spindles for reduced maintenance",
    "Wheel Balancing included",
    "Galvanized Hardware, U-bolts, Winch Stand, Axles, Tongue",
    "Hot dipped galvanized finish for superior corrosion resistance",
    "Tubular frame design protects lights wiring and brake lines from damage"
  ];

  return (
    <div className="galvanized-page">
      <SEOHead
        title="Galvanized Boat Trailers - Heavy Duty Steel | Fast Boat Trailers"
        description="Galvanized boat trailers from Fast Boat Trailers in Edgewater, MD. Heavy-duty galvanized steel construction with superior corrosion protection. Bunk, roller, and PWC models available."
        keywords="galvanized boat trailers, galvanized steel trailers, heavy duty trailers Maryland, corrosion resistant trailers, saltwater trailers"
        ogTitle="Galvanized Boat Trailers - Heavy Duty Steel"
        ogDescription="Galvanized boat trailers with heavy-duty steel construction and superior corrosion protection for saltwater and freshwater use."
        ogImage="/images/trailers/galvanized-bunk.jpg"
        canonical="https://fastboattrailers.com/trailers/galvanized"
      />
      <div className="container">
        {/* Hero Section */}
        <div className="galvanized-hero">
          <div className="hero-main">
            <div className="hero-content">
              <h1>Galvanized Series</h1>
              <h2>Built to Last a Lifetime</h2>
              <p>
                Venture Trailers' line of galvanized boat trailers comes in a range of sizes and configurations. 
                You can stand up to corrosion from any marine environment with a hot dipped galvanized finish, 
                so you can have a trailer that lasts a lifetime.
              </p>
              
              <h3>Benefits of Galvanized Boat Trailers</h3>
              <ul className="benefits-list">
                <li>Heavier than aluminum trailers</li>
                <li>Excellent corrosion resistance, season after season</li>
                <li>Tubular frame design protects lights wiring and brake lines from damage</li>
              </ul>
              
            </div>
            <div className="hero-features">
              <h3>Standard Features</h3>
              <div className="features-compact">
                {galvanizedFeatures.map((feature, index) => (
                  <div key={index} className="feature-item-compact">
                    <div className="feature-icon-compact">✓</div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bunk Trailers Section */}
        <div className="models-section">
          <h2>Galvanized Bunk Trailers</h2>
          
          {/* Single Axle Bunk Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-single-axle-bunk.jpg" alt="Galvanized Single Axle Bunk Trailer" />
            </div>
            <div className="category-header">
              <h3>Single Axle Bunk Models</h3>
              <p>Perfect for smaller to mid-size boats requiring dependable galvanized protection.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {singleAxleBunkModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tandem Axle Bunk Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-tandem-axle-bunk.jpg" alt="Galvanized Tandem Axle Bunk Trailer" />
            </div>
            <div className="category-header">
              <h3>Tandem Axle Bunk Models</h3>
              <p>Enhanced load distribution and stability for larger boats in challenging marine environments.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {tandemAxleBunkModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Triple Axle Bunk Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-triple-axle-bunk.jpg" alt="Galvanized Triple Axle Bunk Trailer" />
            </div>
            <div className="category-header">
              <h3>Triple Axle Bunk Models</h3>
              <p>Maximum capacity and corrosion protection for the largest boats and most demanding conditions.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {tripleAxleBunkModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* PWC Section */}
        <div className="models-section">
          <h2>Personal Watercraft Trailers</h2>
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-pwc.jpg" alt="Galvanized PWC Trailer" />
            </div>
            <div className="category-header">
              <h3>Galvanized PWC Models</h3>
              <p>Corrosion-resistant trailers designed specifically for personal watercraft.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Seating</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {pwcModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.seating}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pontoon Section */}
        <div className="models-section">
          <h2>Pontoon Trailers</h2>
          
          {/* Single Axle Pontoon Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-pontoon-single-axle.jpg" alt="Galvanized Single Axle Pontoon Trailer" />
            </div>
            <div className="category-header">
              <h3>Single Axle Pontoon Models</h3>
              <p>Perfect for smaller pontoon boats requiring reliable galvanized transport.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {singleAxlePontoonModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tandem Axle Pontoon Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-pontoon-tandem-axle.jpg" alt="Galvanized Tandem Axle Pontoon Trailer" />
            </div>
            <div className="category-header">
              <h3>Tandem Axle Pontoon Models</h3>
              <p>Enhanced capacity and stability for larger pontoon boats.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {tandemAxlePontoonModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Triple Axle Pontoon Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-pontoon-triple-axle.jpg" alt="Galvanized Triple Axle Pontoon Trailer" />
            </div>
            <div className="category-header">
              <h3>Triple Axle Pontoon Models</h3>
              <p>Maximum capacity for the largest pontoon boats.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {tripleAxlePontoonModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Roller Trailers Section */}
        <div className="models-section">
          <h2>Roller Trailers</h2>
          
          {/* Single Axle Roller Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-roller-single-axle.jpg" alt="Galvanized Single Axle Roller Trailer" />
            </div>
            <div className="category-header">
              <h3>Single Axle Roller Models</h3>
              <p>Smooth launching and retrieving with galvanized roller systems.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Rollers</th>
                    <th>Brakes</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {singleAxleRollerModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.rollers}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tandem Axle Roller Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-roller-tandem-axle.jpg" alt="Galvanized Tandem Axle Roller Trailer" />
            </div>
            <div className="category-header">
              <h3>Tandem Axle Roller Models</h3>
              <p>Enhanced stability and capacity for larger boats with roller systems.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Rollers</th>
                    <th>Brakes</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {tandemAxleRollerModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.rollers}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Triple Axle Roller Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-roller-triple-axle.jpg" alt="Galvanized Triple Axle Roller Trailer" />
            </div>
            <div className="category-header">
              <h3>Triple Axle Roller Models</h3>
              <p>Maximum capacity roller trailers for the largest boats.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Rollers</th>
                    <th>Brakes</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {tripleAxleRollerModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.rollers}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Tritoon Section */}
        <div className="models-section">
          <h2>Tritoon Trailers</h2>
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-pontoon-single-axle.jpg" alt="Galvanized Tritoon Trailer" />
            </div>
            <div className="category-header">
              <h3>Galvanized Tritoon Models</h3>
              <p>Specially designed for tritoon boats with three pontoons requiring adjusted bunk spacing.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Axles</th>
                    <th>Brakes</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {tritoonModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.axles}</td>
                      <td>{model.brakes}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Skiff and Jon Boat Section */}
        <div className="models-section">
          <h2>Skiff and Jon Boat Trailers</h2>
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/galvanized-single-axle-bunk.jpg" alt="Galvanized Skiff and Jon Boat Trailer" />
            </div>
            <div className="category-header">
              <h3>Galvanized Skiff Models</h3>
              <p>Compact, galvanized trailers perfect for skiffs and jon boats.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {skiffModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="galvanized-cta">
          <h2>Ready for a Galvanized Trailer?</h2>
          <p>
            Built to withstand the harshest marine environments, our galvanized trailers offer unmatched 
            corrosion resistance and durability. Trust your investment to a trailer that's built to last a lifetime.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">Get a quote</Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GalvanizedTrailersPage